import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DraftFunction } from 'use-immer';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { WizardValidationError } from '@/core/types/Wizard';

import ErrorTag from '@/core/components/elements/error-tag';

import classes from './styles.module.scss';

interface Props {
    password: string;
    confirmPassword: string;
    updatePassword: (
        arg: Record<string, string> | DraftFunction<Record<string, string>>,
    ) => void;
    validationErrors: WizardValidationError[];
}

export default function AccountDetails({
    password,
    confirmPassword,
    updatePassword,
    validationErrors,
}: Props) {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        updatePassword((draft) => {
            draft[name] = value;
        });
    };

    const formErrors = useMemo(() => {
        const inputNames: string[] = [
            'password',
            'confirmPassword',
            'passwordMissmatch',
            'passwordInvalid',
        ];

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [validationErrors]);

    return (
        <div>
            <span className={`${classes.header_text}`}>
                {t('wizard:accountDetails:title')}
            </span>

            {formErrors.map((error, index) => (
                <ErrorTag key={`${index}_error_idx`} error={error} />
            ))}

            <div className={`${classes.form_container}`}>
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder={t('wizard:accountDetails:password')}
                        onChange={handlePasswordChange}
                        value={password}
                        minLength={8}
                        maxLength={20}
                        style={{ paddingRight: '40px' }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '5px',
                        }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? (
                            <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.25rem', width: '.7em' }} />
                        ) : (
                            <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.25rem', width: '.7em' }} />
                        )}
                    </button>
                </div>

                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder={t('wizard:accountDetails:confirmPassword')}
                        onChange={handlePasswordChange}
                        value={confirmPassword}
                        minLength={8}
                        maxLength={20}
                        style={{ paddingRight: '40px' }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '5px',
                        }}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                        {showConfirmPassword ? (
                            <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.25rem', width: '.7em' }} />
                        ) : (
                            <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.25rem', width: '.7em' }} />
                        )}
                    </button>
                </div>
            </div>
            <label className={`${classes.copy}`}>
                {t('wizard:accountDetails:copy')}
            </label>
        </div>
    );
}
